import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SkipForward, X } from "lucide-react";
import { Api } from "../lib/api.js";

/**
 * "Next episode" helper. Given the currently-playing item, look up its
 * series episode list and figure out the next one. Renders:
 *   - A persistent pill in the action stack ("Next: S01 E04")
 *   - An auto-advance prompt that the Watch page can mount when playback
 *     ends (5-second countdown, cancellable)
 *
 * Works for both local tv_show items (looked up by category) and Real-Debrid
 * virtual series episodes (id like rd:series:tt12345:S01E04).
 */

export interface NextEpisodeInfo {
  id: string;
  title: string;
  season: number | null;
  episode: number | null;
  episodeTitle: string;
}

export function useNextEpisode(currentItem: {
  id: string;
  kind: string;
  category?: string | null;
}, profileId: string | null): NextEpisodeInfo | null {
  // Local series — lookup by category.
  const localQ = useQuery({
    queryKey: ["series-next-local", currentItem.category, profileId],
    queryFn: () => Api.library.seriesEpisodes(currentItem.category!, profileId ?? undefined),
    enabled: currentItem.kind === "tv_show" && !!currentItem.category && !currentItem.id.startsWith("rd:"),
    staleTime: 60_000,
  });

  // RD virtual series — extract IMDb id from the current rd: id.
  const rdMatch = /^rd:series:(tt\d+)(?::S\d{1,3}E\d{1,3})?$/.exec(currentItem.id);
  const rdImdb = rdMatch ? rdMatch[1]! : null;
  const rdQ = useQuery({
    queryKey: ["series-next-rd", rdImdb, profileId],
    queryFn: () => Api.library.seriesEpisodesRd(rdImdb!, profileId ?? undefined),
    enabled: !!rdImdb,
    staleTime: 60_000,
  });

  return useMemo<NextEpisodeInfo | null>(() => {
    // Local TV path
    if (currentItem.kind === "tv_show" && localQ.data) {
      const sorted = [...localQ.data.items].sort((a, b) => a.title.localeCompare(b.title));
      const idx = sorted.findIndex((it) => it.id === currentItem.id);
      if (idx === -1 || idx + 1 >= sorted.length) return null;
      const next = sorted[idx + 1]!;
      const parsed = parseSE(next.title, localQ.data.series_name);
      return {
        id: next.id,
        title: next.title,
        season: parsed.season,
        episode: parsed.episode,
        episodeTitle: parsed.episodeTitle || next.title,
      };
    }
    // RD virtual path
    if (rdImdb && rdQ.data) {
      const sorted = [...rdQ.data.items].sort((a, b) =>
        a.season !== b.season ? a.season - b.season : a.episode - b.episode,
      );
      const idx = sorted.findIndex((it) => it.id === currentItem.id);
      if (idx === -1 || idx + 1 >= sorted.length) return null;
      const next = sorted[idx + 1]!;
      return {
        id: next.id,
        title: next.title,
        season: next.season,
        episode: next.episode,
        episodeTitle: next.episodeTitle,
      };
    }
    return null;
  }, [currentItem.id, currentItem.kind, localQ.data, rdQ.data, rdImdb]);
}

function parseSE(rawTitle: string, seriesName: string): { season: number | null; episode: number | null; episodeTitle: string } {
  const stripped = rawTitle.startsWith(seriesName) ? rawTitle.slice(seriesName.length).trim() : rawTitle;
  const m = /^S(\d{1,3})\s*E(\d{1,3})(.*)$/i.exec(stripped);
  if (!m) return { season: null, episode: null, episodeTitle: stripped };
  return {
    season: Number(m[1]),
    episode: Number(m[2]),
    episodeTitle: (m[3] ?? "").replace(/^[\s\-—:]+/, "").trim(),
  };
}

export function NextEpisodeButton({ next }: { next: NextEpisodeInfo }): JSX.Element {
  return (
    <Link
      to={`/watch/${encodeURIComponent(next.id)}`}
      className="pill-ghost"
      title={next.episodeTitle}
    >
      <SkipForward size={14} />
      Next: S{String(next.season ?? 0).padStart(2, "0")}E{String(next.episode ?? 0).padStart(2, "0")}
    </Link>
  );
}

/**
 * Modal-ish auto-advance prompt. Mount when player.onEnded fires. Counts
 * down from N seconds and navigates unless the user cancels.
 */
export function AutoAdvancePrompt({
  next,
  seconds = 8,
  onAdvance,
  onCancel,
}: {
  next: NextEpisodeInfo;
  seconds?: number;
  onAdvance: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [remaining, setRemaining] = useState(seconds);
  useEffect(() => {
    if (remaining <= 0) { onAdvance(); return; }
    const t = window.setTimeout(() => setRemaining(remaining - 1), 1000);
    return () => window.clearTimeout(t);
  }, [remaining, onAdvance]);
  return (
    <div className="absolute inset-x-0 bottom-0 z-20 p-6 flex items-end justify-end pointer-events-none">
      <div className="pointer-events-auto card bg-lacquer/95 backdrop-blur border border-gold/40 px-5 py-4 flex items-center gap-4 shadow-2xl max-w-md">
        <div className="flex-1">
          <div className="section-label text-gold">UP NEXT IN {remaining}s</div>
          <div className="text-ivory font-display tracking-smallcaps mt-1 text-sm">
            S{String(next.season ?? 0).padStart(2, "0")}E{String(next.episode ?? 0).padStart(2, "0")}
            {next.episodeTitle && <> — {next.episodeTitle}</>}
          </div>
        </div>
        <button
          onClick={onAdvance}
          className="pill bg-vermilion border-goldHi"
        >
          <SkipForward size={14} /> Play now
        </button>
        <button
          onClick={onCancel}
          className="pill-ghost p-1.5"
          aria-label="Cancel auto-advance"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
